// Utils
import globalLogger from '../../logger/logger';
import { prepareActionForLogging } from '../../../common/logger/loggerUtils';

// Selectors
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { handleElementUpdate } from './canvasSizeMiddlewareHandleElementUpdateUtils';
import { handleElementMultiMove } from './canvasSizeMiddlewareHandleElementMoveUtils';
import { handleElementCreate } from './canvasSizeMiddlewareHandleElementCreateUtils';
import { validateAction } from './canvasSizeMiddlewareValidateActionUtils';

// Actions
import { openErrorNotice } from '../../components/error/errorNoticeActions';

// Constants
import { ELEMENT_CREATE, ELEMENT_MOVE_MULTI, ELEMENT_UPDATE } from '../../../common/elements/elementConstants';
import { BOARD_ERROR } from '../../../common/error/errorConstants';

// Types
import { ReduxStore } from '../../types/reduxTypes';
import { Rect } from '../../../common/maths/geometry/rect/rectTypes';
import { ActionObject } from '../../../common/actions/actionTypes';
import { UnknownAction } from 'redux';

interface MeasurementsMap {
    [id: string]: Rect;
}

interface ClientActionObject extends ActionObject {
    initialMeasurements?: MeasurementsMap;
}

const logger = globalLogger.createChannel('canvas-size-middleware');

/**
 * Update the canvas size depending on the action type.
 */
const handleAction = (action: ClientActionObject, store: ReduxStore, measurementsStore: ReduxStore) => {
    switch (action.type) {
        // If the canvas origin has changed - we need to update the measurements for canvas elements
        case ELEMENT_UPDATE:
            return handleElementUpdate(action, store, measurementsStore);
        // On create ensure the canvas is large enough to include the new element
        case ELEMENT_CREATE:
            return handleElementCreate(action, store);
        // On move ensure that the canvas is large enough to include the newly moved elements
        case ELEMENT_MOVE_MULTI:
            return handleElementMultiMove(action, store, measurementsStore);
        default:
            return;
    }
};

/**
 * Changes the canvas size on certain actions, such as creating elements.
 */
export default ({ measurementsStore }: { measurementsStore: ReduxStore }) =>
    (store: ReduxStore) =>
    (next: Function) =>
    (action: ClientActionObject) => {
        try {
            validateAction(action, store);
            handleAction(action, store, measurementsStore);
            // I couldn't find a better way to handle this in TS :/
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            // Sensitive data audit: SAFE — Errors are sanitised, action is prepared for logging
            const errorMessage = `Failed to update canvas origin - ${error.message}`;
            logger.error(errorMessage, error, {
                ...prepareActionForLogging(action as UnknownAction),
                details: error?.details,
                fingerprint: error?.fingerprint,
            });
            store.dispatch(
                openErrorNotice({
                    errorNoticeId: BOARD_ERROR.CANVAS_SIZE,
                    // @ts-ignore Structure of error modal data is not defined
                    data: {
                        error,
                        boardId: getCurrentBoardId(store.getState()),
                    },
                }),
            );

            // Don't process the action if there's an error with the move
            return;
        }

        return next(action);
    };
